import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { Search, AlertTriangle, Ban, Eye } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { formatGNF } from "@/utils/format";
import { toast } from "sonner";
import ClientDetailModal from "./ClientDetailModal";

/** Badge de risque selon le taux d'annulation (seuil configurable). */
function RiskBadge({ rate, flagged, userId }) {
  const { t } = useTranslation();
  if (!flagged) return null;
  const red = rate >= 50;
  return (
    <span
      data-testid={`client-risk-badge-${userId}`}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${red ? "bg-brand-red text-white" : "bg-[#FFF3CD] text-[#946A00]"}`}
    >
      <AlertTriangle className="w-3 h-3" /> {red ? t("admin.riskHigh") : t("admin.riskWatch")}
    </span>
  );
}

/** Onglet admin "Clients" : indicateurs comportementaux par client
 * (commandes, annulations, taux, remboursements) + fiche détaillée + blocage. */
export default function ClientsPanel() {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("rate"); // rate | orders | refunded | name
  const [riskOnly, setRiskOnly] = useState(false);
  const [threshold, setThreshold] = useState(30); // % configurable
  const [minOrders, setMinOrders] = useState(3);  // min commandes pour flaguer
  const [detailId, setDetailId] = useState(null);

  const load = () => {
    apiClient.get("/admin/clients")
      .then(({ data }) => setClients(data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const isFlagged = (c) => c.total_orders >= minOrders && c.cancel_rate >= threshold;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const qDigits = q.replace(/[^\d]/g, "");
    let list = clients.filter((c) => {
      if (riskOnly && !isFlagged(c)) return false;
      if (!q) return true;
      const phone = (c.phone || "").replace(/[^\d]/g, "");
      return (c.name || "").toLowerCase().includes(q)
        || (c.email || "").toLowerCase().includes(q)
        || (qDigits && phone.includes(qDigits));
    });
    const sorters = {
      rate: (a, b) => b.cancel_rate - a.cancel_rate || b.total_orders - a.total_orders,
      orders: (a, b) => b.total_orders - a.total_orders,
      refunded: (a, b) => b.refunded_total - a.refunded_total,
      name: (a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" }),
    };
    return [...list].sort(sorters[sortBy]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, search, sortBy, riskOnly, threshold, minOrders]);

  const flaggedCount = clients.filter(isFlagged).length;

  return (
    <div className="space-y-3" data-testid="admin-clients-panel">
      {/* Barre d'outils : recherche, tri, seuil configurable */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative max-w-xs w-full">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-mute" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("admin.searchClient")}
            data-testid="clients-search"
            className="w-full bg-white border border-sand rounded-full pl-10 pr-4 py-2.5 text-sm outline-none focus:border-brand-red"
          />
        </div>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="clients-sort"
          className="bg-white border border-sand rounded-full px-3.5 py-2.5 text-sm outline-none">
          <option value="rate">Taux d'annulation ↓</option>
          <option value="orders">Nb commandes ↓</option>
          <option value="refunded">{t("admin.sortRefunded")}</option>
          <option value="name">Nom A→Z</option>
        </select>
        <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer select-none">
          <input type="checkbox" checked={riskOnly} onChange={(e) => setRiskOnly(e.target.checked)}
            data-testid="clients-risk-only" className="w-4 h-4 accent-brand-red" />
          Clients à risque uniquement {flaggedCount > 0 && <span className="text-xs text-brand-red font-bold">({flaggedCount})</span>}
        </label>
        <div className="inline-flex items-center gap-1.5 text-xs text-mute ml-auto">
          Seuil :
          <input type="number" min={1} max={100} value={threshold}
            onChange={(e) => setThreshold(Math.max(1, Math.min(100, parseInt(e.target.value) || 30)))}
            data-testid="clients-threshold"
            className="w-14 bg-white border border-sand rounded-lg px-2 py-1.5 text-sm outline-none text-center" />
          % dès
          <input type="number" min={1} max={50} value={minOrders}
            onChange={(e) => setMinOrders(Math.max(1, Math.min(50, parseInt(e.target.value) || 3)))}
            data-testid="clients-min-orders"
            className="w-12 bg-white border border-sand rounded-lg px-2 py-1.5 text-sm outline-none text-center" />
          commandes
        </div>
      </div>

      <div className="bg-white border border-sand rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-elevated text-mute uppercase text-xs tracking-widest">
            <tr>
              <th className="p-3 text-left">Client</th>
              <th>{t("admin.thPhone")}</th>
              <th>Commandes</th>
              <th>{t("admin.thDelivered")}</th>
              <th>{t("admin.thCancelled")}</th>
              <th>Taux annulation</th>
              <th>{t("admin.thRefunded")}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const flagged = isFlagged(c);
              return (
                <tr key={c.user_id} className={`border-t border-sand ${flagged ? "bg-[#FFFBF2]" : ""}`} data-testid={`client-row-${c.user_id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold">{c.name}</span>
                      {c.blocked && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold bg-ink text-white px-2 py-0.5 rounded-full" data-testid={`client-blocked-tag-${c.user_id}`}>
                          <Ban className="w-3 h-3" /> bloqué
                        </span>
                      )}
                      <RiskBadge rate={c.cancel_rate} flagged={flagged} userId={c.user_id} />
                    </div>
                    {c.email && <div className="text-[11px] text-mute">{c.email}</div>}
                  </td>
                  <td className="text-center">{c.phone || "—"}</td>
                  <td className="text-center font-semibold">{c.total_orders}</td>
                  <td className="text-center text-brand-green">{c.delivered}</td>
                  <td className="text-center">{c.cancelled}{c.cancelled_total > c.cancelled ? <span className="text-[10px] text-mute"> (+{c.cancelled_total - c.cancelled} autres)</span> : ""}</td>
                  <td className="text-center">
                    <span className={`font-bold ${c.cancel_rate >= 50 ? "text-brand-red" : flagged ? "text-[#946A00]" : ""}`} data-testid={`client-rate-${c.user_id}`}>
                      {c.cancel_rate}%
                    </span>
                  </td>
                  <td className="text-center">{c.refunded_total ? formatGNF(c.refunded_total) : "—"}</td>
                  <td className="text-center p-2">
                    <button onClick={() => setDetailId(c.user_id)} data-testid={`client-detail-btn-${c.user_id}`}
                      className="inline-flex items-center gap-1 text-xs rounded-full border border-sand px-2.5 py-1 hover:border-ink">
                      <Eye className="w-3 h-3" /> Fiche
                    </button>
                  </td>
                </tr>
              );
            })}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-mute text-sm" data-testid="clients-empty">{t("admin.noClient")}</td></tr>
            )}
            {loading && (
              <tr><td colSpan={8} className="p-6 text-center text-mute text-sm">Chargement…</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {detailId && (
        <ClientDetailModal userId={detailId} onClose={() => setDetailId(null)} onChanged={load} />
      )}
    </div>
  );
}
